import React, { useState, useContext } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isVisible } from '../../utils';
import Modal from '../shared/modal/Modal';
import ApproachContext from '../../context/ApproachContext';

import 'react-responsive-carousel/lib/styles/carousel.css';

// PropTypes
import { companyPropTypes } from '../../proptypes/companyProptypes';

// Styles
import styles from './imageGallery.module.scss';

const Carousel = dynamic(() =>
  import('react-responsive-carousel').then((mod) => mod.Carousel),
);

const cx = classnames.bind(styles);

const ImageGallery = ({ images, showFullscreen, setShowFullscreen }) => {
  const { t } = useTranslation('common');

  const approach = useContext(ApproachContext);

  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [smallSliderLeft, setSmallSliderLeft] = useState(0);

  return (
    <div className={styles.container}>
      <div className={styles.relativeWrapper}>
        <div
          id="gallery-wrapper"
          className={styles.gallery}
          style={{
            left: smallSliderLeft,
          }}
        >
          {images.map((image, index) => (
            <div
              key={`${image}-preview-${index}`}
              id={`gallery-image-${index}`}
              className={cx(styles.galleryImage, {
                [styles.active]: currentImgIndex === index,
              })}
              onClick={async () => {
                if (
                  !(await isVisible(
                    document.getElementById(`gallery-image-${index}`),
                  ))
                ) {
                  setSmallSliderLeft((left) =>
                    index > currentImgIndex ? left - 180 : left + 180,
                  );
                }
                setCurrentImgIndex(index);
                setShowFullscreen(true);
              }}
            >
              <Image
                src={`${image}`}
                alt={`company image ${index + 1} preview`}
                width={160}
                height={128}
                objectFit="cover"
                priority
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.controls}>
        <button
          className={styles.buttonPrevious}
          type="button"
          onClick={() => {
            if (currentImgIndex > 0) {
              setCurrentImgIndex((prevVal) => prevVal - 1);
              if (smallSliderLeft < 0) setSmallSliderLeft((left) => left + 180);
            }
          }}
        >
          <img
            src="/static/prev.svg"
            alt="previous icon"
            className={styles.navIcon}
          />
        </button>
        <span>
          {' '}
          {currentImgIndex + 1} <span>/</span> {images.length}{' '}
          {t(`description_page.about_company_section.photos.${approach}`)}
        </span>
        <button
          className={styles.buttonNext}
          type="button"
          onClick={async () => {
            if (currentImgIndex < images.length - 1) {
              setCurrentImgIndex((prevVal) => prevVal + 1);
              const imageElement = document.getElementById(
                `gallery-image-${currentImgIndex + 1}`,
              );
              if (!(await isVisible(imageElement))) {
                setSmallSliderLeft((left) => left - 180);
              }
            }
          }}
        >
          <img
            src="/static/next.svg"
            alt="next icon"
            className={styles.navIcon}
          />
        </button>
      </div>

      {showFullscreen && (
        <Modal isOpen={showFullscreen} onClose={() => setShowFullscreen(false)}>
          <div className={styles.carousel}>
            <Carousel
              selectedItem={currentImgIndex}
              showArrows
              dynamicHeight
              showThumbs={false}
            >
              {images.map((image, index) => (
                <div
                  key={`${image}-full-${index}`}
                  className={styles.carouselImage}
                >
                  <Image
                    key={image}
                    src={image}
                    alt={`company image ${index + 1}`}
                    layout="fill"
                    objectFit="contain"
                  />
                </div>
              ))}
            </Carousel>
          </div>
        </Modal>
      )}
    </div>
  );
};

ImageGallery.propTypes = {
  images: companyPropTypes.pictures.isRequired,
  showFullscreen: PropTypes.bool.isRequired,
  setShowFullscreen: PropTypes.func.isRequired,
};

export default ImageGallery;
